import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

const translations = {
  en: {
    // Navigation
    nav: {
      home: 'Home',
      about: 'About Us',
      services: 'Services',
      howItWorks: 'How It Works',
      contact: 'Contact',
    },
    // Hero Section
    hero: {
      title: 'Your Gateway to German Products',
      subtitle: 'Connecting international buyers with premium German warehouses for seamless global trade.',
      cta: 'Request a Quote',
      ctaSecondary: 'Learn More',
    },
    // About Section
    about: {
      title: 'About GermanExpo Solutions',
      subtitle: 'Your Trusted Partner in Global Trade',
      description: 'GermanExpo Solutions acts as a professional mediator between international buyers and German warehouses. We specialize in facilitating seamless transactions, ensuring quality products reach global markets efficiently.',
      experience: 'With years of experience in international trade and logistics, we have built a network of trusted warehouse partners across Germany. Our team of experts ensures that every order is processed with the highest standards of quality and efficiency.',
      point1Title: 'Reliability',
      point1Desc: 'We ensure every transaction is handled with the utmost professionalism and care.',
      point2Title: 'Transparency',
      point2Desc: 'Clear communication and honest pricing throughout the entire process.',
      point3Title: 'Global Reach',
      point3Desc: 'Connecting businesses across continents with German quality products.',
      coreValuesTitle: 'Our Core Values',
      coreValuesSubtitle: 'These principles guide everything we do at GermanExpo Solutions',
      ourClients: 'Our Clients',
      europe: 'Europe',
      middleEast: 'Middle-East',
      northAfrica: 'North Africa',
      missionTitle: 'Our Mission',
      missionDesc: 'To bridge the gap between international buyers and German quality products, providing seamless trade solutions that empower businesses worldwide. We strive to make German products accessible to every corner of the globe through our reliable, transparent, and efficient services.',
    },
    // Services Section
    services: {
      title: 'Our Services',
      subtitle: 'Comprehensive solutions for your international trade needs',
      service1Title: 'Warehouse Mediation',
      service1Desc: 'We connect you with verified German warehouses, ensuring you get the best products at competitive prices.',
      service2Title: 'Logistics Coordination',
      service2Desc: 'End-to-end logistics management including customs clearance and international shipping.',
      service3Title: 'Quality Assurance',
      service3Desc: 'Rigorous quality checks to ensure products meet international standards before shipping.',
      service4Title: 'Export Documentation',
      service4Desc: 'Complete handling of all export paperwork and compliance requirements.',
      additionalTitle: 'Additional Services',
      additionalSubtitle: 'Beyond our core offerings, we provide these value-added services',
      packaging: 'Packaging Solutions',
      packagingDesc: 'Custom packaging for safe international transit',
      marketResearch: 'Market Research',
      marketResearchDesc: 'Insights into German product markets and trends',
      support: '24/7 Support',
      supportDesc: 'Dedicated customer support for all your queries',
      globalTradeTitle: 'Global Trade Made Simple',
      globalTradeDesc1: 'Our comprehensive services are designed to handle every aspect of your international trade needs. From the initial contact to the final delivery, we ensure a smooth and hassle-free experience.',
      globalTradeDesc2: 'Partner with us and unlock access to Germany\'s finest products through our extensive network of verified warehouses and logistics partners.',
    },
    // How It Works Section
    howItWorks: {
      title: 'How It Works',
      subtitle: 'Simple steps to get your products from Germany',
      step1Title: 'Contact Us',
      step1Desc: 'Reach out with your product requirements and specifications.',
      step1Details: 'Reach out to us via our contact form, email, or phone. Share your product requirements, quantities, and any specific specifications you need.',
      step2Title: 'Quote & Confirmation',
      step2Desc: 'Receive a detailed quote and confirm your order.',
      step2Details: 'We source products from our network of German warehouses and provide you with a detailed quote including product costs, shipping, and any applicable fees.',
      step3Title: 'Order Processing',
      step3Desc: 'We coordinate with warehouses and prepare your shipment.',
      step3Details: 'Once you confirm the order, we handle the procurement, quality checks, packaging, and prepare all necessary export documentation.',
      step4Title: 'Shipping',
      step4Desc: 'Your products are shipped safely to your destination.',
      step4Details: 'Your products are shipped to your destination with real-time tracking. We handle customs clearance and ensure timely delivery.',
      whyWorkWithUs: 'Why Work With Us?',
      whyWorkWithUsDesc: 'We make international trade simple and accessible. Our streamlined process ensures you get quality German products delivered to your doorstep with minimal hassle.',
      benefit1: 'No minimum order requirements',
      benefit2: 'Transparent pricing with no hidden fees',
      benefit3: 'Quality guarantee on all products',
      benefit4: 'Dedicated account manager',
      benefit5: 'Flexible payment options',
      benefit6: 'Insurance coverage available',
      readyToStart: 'Ready to Get Started?',
      readyToStartDesc: 'Contact us today and let us help you source the best German products for your business.',
    },
    // Contact Section
    contact: {
      title: 'Contact Us',
      subtitle: 'Get in touch with our team',
      sendMessage: 'Send us a Message',
      nameLabel: 'Full Name',
      namePlaceholder: 'Enter your name',
      emailLabel: 'Email Address',
      emailPlaceholder: 'Enter your email',
      phoneLabel: 'Phone Number',
      phonePlaceholder: 'Enter your phone number',
      addressLabel: 'Address',
      addressPlaceholder: 'Enter your address',
      messageLabel: 'Message',
      messagePlaceholder: 'How can we help you?',
      submitBtn: 'Send Message',
      successMessage: 'Thank you! Your message has been sent successfully.',
      errorMessage: 'Something went wrong. Please try again.',
      contactInfo: 'Contact Information',
      contactInfoDesc: 'Have questions or need a quote? We\'re here to help. Reach out to us through any of the channels below, and our team will get back to you as soon as possible.',
      businessEmail: 'Business Email',
      officeHours: 'Office Hours',
      officeHoursValue: 'Mon - Fri: 9:00 AM - 6:00 PM CET',
      location: 'Location',
    },
    // Footer
    footer: {
      description: 'Connecting international buyers with German warehouses for seamless global trade.',
      quickLinks: 'Quick Links',
      legal: 'Legal',
      imprint: 'Imprint',
      privacy: 'Privacy Policy',
      rights: 'All rights reserved.',
    },
  },
  ar: {
    // Navigation
    nav: {
      home: 'الرئيسية',
      about: 'من نحن',
      services: 'خدماتنا',
      howItWorks: 'كيف نعمل',
      contact: 'اتصل بنا',
    },
    // Hero Section
    hero: {
      title: 'بوابتك للمنتجات الألمانية',
      subtitle: 'نربط المشترين الدوليين بالمستودعات الألمانية الممتازة لتجارة عالمية سلسة.',
      cta: 'طلب عرض سعر',
      ctaSecondary: 'اعرف المزيد',
    },
    // About Section
    about: {
      title: 'عن جيرمان إكسبو سوليوشنز',
      subtitle: 'شريكك الموثوق في التجارة العالمية',
      description: 'تعمل جيرمان إكسبو سوليوشنز كوسيط محترف بين المشترين الدوليين والمستودعات الألمانية. نحن متخصصون في تسهيل المعاملات السلسة، وضمان وصول المنتجات عالية الجودة إلى الأسواق العالمية بكفاءة.',
      experience: 'مع سنوات من الخبرة في التجارة الدولية والخدمات اللوجستية، قمنا ببناء شبكة من شركاء المستودعات الموثوقين في جميع أنحاء ألمانيا. يضمن فريق خبرائنا معالجة كل طلب بأعلى معايير الجودة والكفاءة.',
      point1Title: 'الموثوقية',
      point1Desc: 'نضمن التعامل مع كل معاملة بأقصى درجات المهنية والعناية.',
      point2Title: 'الشفافية',
      point2Desc: 'تواصل واضح وأسعار صادقة طوال العملية بأكملها.',
      point3Title: 'الوصول العالمي',
      point3Desc: 'ربط الشركات عبر القارات بمنتجات ألمانية عالية الجودة.',
      coreValuesTitle: 'قيمنا الأساسية',
      coreValuesSubtitle: 'هذه المبادئ توجه كل ما نقوم به في جيرمان إكسبو سوليوشنز',
      ourClients: 'عملاؤنا',
      europe: 'أوروبا',
      middleEast: 'الشرق الأوسط',
      northAfrica: 'شمال أفريقيا',
      missionTitle: 'مهمتنا',
      missionDesc: 'سد الفجوة بين المشترين الدوليين والمنتجات الألمانية عالية الجودة، وتوفير حلول تجارية سلسة تمكّن الشركات في جميع أنحاء العالم. نسعى لجعل المنتجات الألمانية في متناول كل ركن من أركان العالم من خلال خدماتنا الموثوقة والشفافة والفعالة.',
    },
    // Services Section
    services: {
      title: 'خدماتنا',
      subtitle: 'حلول شاملة لاحتياجات تجارتك الدولية',
      service1Title: 'وساطة المستودعات',
      service1Desc: 'نربطك بالمستودعات الألمانية المعتمدة، لضمان حصولك على أفضل المنتجات بأسعار تنافسية.',
      service2Title: 'تنسيق الخدمات اللوجستية',
      service2Desc: 'إدارة لوجستية شاملة تشمل التخليص الجمركي والشحن الدولي.',
      service3Title: 'ضمان الجودة',
      service3Desc: 'فحوصات جودة صارمة لضمان توافق المنتجات مع المعايير الدولية قبل الشحن.',
      service4Title: 'وثائق التصدير',
      service4Desc: 'التعامل الكامل مع جميع أوراق التصدير ومتطلبات الامتثال.',
      additionalTitle: 'خدمات إضافية',
      additionalSubtitle: 'بالإضافة إلى عروضنا الأساسية، نقدم هذه الخدمات ذات القيمة المضافة',
      packaging: 'حلول التغليف',
      packagingDesc: 'تغليف مخصص للنقل الدولي الآمن',
      marketResearch: 'أبحاث السوق',
      marketResearchDesc: 'رؤى حول أسواق المنتجات الألمانية واتجاهاتها',
      support: 'دعم على مدار الساعة',
      supportDesc: 'دعم عملاء مخصص لجميع استفساراتك',
      globalTradeTitle: 'التجارة العالمية أصبحت بسيطة',
      globalTradeDesc1: 'تم تصميم خدماتنا الشاملة للتعامل مع كل جانب من جوانب احتياجات تجارتك الدولية. من الاتصال الأولي إلى التسليم النهائي، نضمن تجربة سلسة وخالية من المتاعب.',
      globalTradeDesc2: 'كن شريكاً معنا واحصل على وصول إلى أفضل المنتجات الألمانية من خلال شبكتنا الواسعة من المستودعات والشركاء اللوجستيين المعتمدين.',
    },
    // How It Works Section
    howItWorks: {
      title: 'كيف نعمل',
      subtitle: 'خطوات بسيطة للحصول على منتجاتك من ألمانيا',
      step1Title: 'تواصل معنا',
      step1Desc: 'تواصل معنا بمتطلبات ومواصفات منتجك.',
      step1Details: 'تواصل معنا عبر نموذج الاتصال أو البريد الإلكتروني أو الهاتف. شاركنا متطلبات منتجك والكميات وأي مواصفات محددة تحتاجها.',
      step2Title: 'العرض والتأكيد',
      step2Desc: 'احصل على عرض سعر مفصل وأكد طلبك.',
      step2Details: 'نقوم بتوفير المنتجات من شبكة مستودعاتنا الألمانية ونقدم لك عرض سعر مفصل يشمل تكاليف المنتج والشحن وأي رسوم مطبقة.',
      step3Title: 'معالجة الطلب',
      step3Desc: 'نتنسق مع المستودعات ونجهز شحنتك.',
      step3Details: 'بمجرد تأكيد الطلب، نتولى عملية الشراء وفحوصات الجودة والتغليف وإعداد جميع وثائق التصدير اللازمة.',
      step4Title: 'الشحن',
      step4Desc: 'يتم شحن منتجاتك بأمان إلى وجهتك.',
      step4Details: 'يتم شحن منتجاتك إلى وجهتك مع تتبع في الوقت الفعلي. نتولى التخليص الجمركي ونضمن التسليم في الوقت المحدد.',
      whyWorkWithUs: 'لماذا تعمل معنا؟',
      whyWorkWithUsDesc: 'نجعل التجارة الدولية بسيطة ومتاحة. تضمن عمليتنا المبسطة حصولك على منتجات ألمانية عالية الجودة يتم توصيلها إلى باب منزلك بأقل قدر من المتاعب.',
      benefit1: 'لا يوجد حد أدنى للطلبات',
      benefit2: 'أسعار شفافة بدون رسوم مخفية',
      benefit3: 'ضمان الجودة على جميع المنتجات',
      benefit4: 'مدير حساب مخصص',
      benefit5: 'خيارات دفع مرنة',
      benefit6: 'تغطية تأمينية متاحة',
      readyToStart: 'هل أنت مستعد للبدء؟',
      readyToStartDesc: 'تواصل معنا اليوم ودعنا نساعدك في الحصول على أفضل المنتجات الألمانية لعملك.',
    },
    // Contact Section
    contact: {
      title: 'اتصل بنا',
      subtitle: 'تواصل مع فريقنا',
      sendMessage: 'أرسل لنا رسالة',
      nameLabel: 'الاسم الكامل',
      namePlaceholder: 'أدخل اسمك',
      emailLabel: 'البريد الإلكتروني',
      emailPlaceholder: 'أدخل بريدك الإلكتروني',
      phoneLabel: 'رقم الهاتف',
      phonePlaceholder: 'أدخل رقم هاتفك',
      addressLabel: 'العنوان',
      addressPlaceholder: 'أدخل عنوانك',
      messageLabel: 'الرسالة',
      messagePlaceholder: 'كيف يمكننا مساعدتك؟',
      submitBtn: 'إرسال الرسالة',
      successMessage: 'شكراً لك! تم إرسال رسالتك بنجاح.',
      errorMessage: 'حدث خطأ ما. يرجى المحاولة مرة أخرى.',
      contactInfo: 'معلومات الاتصال',
      contactInfoDesc: 'هل لديك أسئلة أو تحتاج إلى عرض سعر؟ نحن هنا للمساعدة. تواصل معنا من خلال أي من القنوات أدناه، وسيتواصل معك فريقنا في أقرب وقت ممكن.',
      businessEmail: 'البريد الإلكتروني للأعمال',
      officeHours: 'ساعات العمل',
      officeHoursValue: 'الإثنين - الجمعة: 9:00 صباحاً - 6:00 مساءً بتوقيت وسط أوروبا',
      location: 'الموقع',
    },
    // Footer
    footer: {
      description: 'ربط المشترين الدوليين بالمستودعات الألمانية لتجارة عالمية سلسة.',
      quickLinks: 'روابط سريعة',
      legal: 'قانوني',
      imprint: 'بيانات الشركة',
      privacy: 'سياسة الخصوصية',
      rights: 'جميع الحقوق محفوظة.',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('en');
  const [isRTL, setIsRTL] = useState(false);

  useEffect(() => {
    const isArabic = language === 'ar';
    setIsRTL(isArabic);
    document.documentElement.dir = isArabic ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'ar' : 'en');
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, isRTL, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
